
import React, { useState, useEffect } from 'react';
import { ref, onValue, update } from 'firebase/database';
import { db } from '../../firebase';
import { Order, CargoCompany } from '../../types';
import { Search, Package, Clock, Truck, CheckCircle2, XCircle, ChevronRight, User, MapPin, Phone, Mail, Hash, ClipboardList, Filter, MessageSquare, Gift, TicketPercent } from 'lucide-react';

const AdminOrders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [companies, setCompanies] = useState<CargoCompany[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [trackingCode, setTrackingCode] = useState('');
  const [selectedCargoId, setSelectedCargoId] = useState('');
  const [activeTab, setActiveTab] = useState<'pending' | 'delivered' | 'all'>('pending');

  useEffect(() => {
    const ordersRef = ref(db, 'orders');
    onValue(ordersRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const ordersList = Object.values(data) as Order[];
        setOrders(ordersList.sort((a, b) => b.createdAt - a.createdAt));
      }
    });

    const cargoRef = ref(db, 'cargoCompanies');
    onValue(cargoRef, (snapshot) => {
      const data = snapshot.val();
      if (data) setCompanies(Object.values(data));
    });
  }, []);

  const updateStatus = async (orderId: string, newStatus: string) => {
    await update(ref(db, `orders/${orderId}`), { status: newStatus });
    if (selectedOrder) setSelectedOrder({...selectedOrder, status: newStatus as any});
  };

  const saveTrackingCode = async (orderId: string) => {
    if (!selectedCargoId) return alert('Lütfen önce bir kargo firması seçin.');
    if (!trackingCode) return alert('Lütfen takip numarasını girin.');
    await update(ref(db, `orders/${orderId}`), { trackingCode, cargoCompanyId: selectedCargoId, status: 'shipped' });
    alert('Kargo bilgileri kaydedildi.');
    setTrackingCode('');
    setSelectedCargoId('');
    if (selectedOrder) setSelectedOrder({...selectedOrder, trackingCode, cargoCompanyId: selectedCargoId, status: 'shipped'});
  };

  const getStatusStyle = (status: string) => {
    switch(status) {
      case 'pending': return 'bg-amber-100 text-amber-700';
      case 'processing': return 'bg-blue-100 text-blue-700';
      case 'shipped': return 'bg-indigo-100 text-indigo-700';
      case 'delivered': return 'bg-emerald-100 text-emerald-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-stone-100 text-stone-700';
    }
  };

  const filteredOrders = orders.filter(o => {
    const matchesSearch = o.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        o.customerInfo.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        o.customerInfo.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        o.userId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        o.couponCode?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (activeTab === 'pending') return matchesSearch && o.status !== 'delivered' && o.status !== 'cancelled';
    if (activeTab === 'delivered') return matchesSearch && o.status === 'delivered';
    return matchesSearch;
  });

  return (
    <div className="p-8 h-full flex flex-col overflow-hidden">
      <div className="flex flex-col md:flex-row items-center justify-between mb-8 gap-4 flex-shrink-0">
        <div>
          <h1 className="text-3xl font-black text-stone-900">Sipariş Yönetimi</h1>
          <p className="text-stone-500 font-medium italic mt-1">Siparişleri izleyin, müşterileri takip edin.</p>
        </div>
        
        <div className="flex bg-white p-1.5 rounded-2xl border border-stone-100 shadow-sm">
          {['pending', 'delivered', 'all'].map((tab) => (
            <button 
              key={tab}
              onClick={() => setActiveTab(tab as any)}
              className={`px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === tab ? 'bg-stone-900 text-white shadow-lg' : 'text-stone-400 hover:text-stone-900'}`}
            >
              {tab === 'pending' ? 'Bekleyenler' : tab === 'delivered' ? 'Teslim Edilenler' : 'Tümü'}
            </button>
          ))}
        </div>

        <div className="relative w-full md:w-72">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
          <input 
            type="text"
            placeholder="İsim, UID veya Kupon ara..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full bg-white border border-stone-100 rounded-2xl py-3 pl-12 pr-4 focus:ring-2 focus:ring-amber-500 outline-none text-sm font-medium"
          />
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-8 flex-1 min-h-0">
        {/* Sipariş Listesi */}
        <div className="lg:col-span-4 bg-white rounded-[2.5rem] border border-stone-100 shadow-sm flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto divide-y divide-stone-50 scrollbar-hide">
            {filteredOrders.length > 0 ? filteredOrders.map((order) => (
              <button 
                key={order.id}
                onClick={() => {
                  setSelectedOrder(order);
                  setSelectedCargoId(order.cargoCompanyId || '');
                  setTrackingCode(order.trackingCode || '');
                }}
                className={`w-full p-6 text-left hover:bg-stone-50 transition-all flex items-center justify-between group ${selectedOrder?.id === order.id ? 'bg-amber-50/50' : ''}`}
              >
                <div className="flex items-center space-x-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${getStatusStyle(order.status)}`}>
                    {order.isGiftWrap ? <Gift size={24} /> : <Package size={24} />}
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <p className="font-black text-stone-900">#{order.id.slice(-8).toUpperCase()}</p>
                      {order.couponCode && <TicketPercent size={14} className="text-emerald-500" />}
                    </div>
                    <p className="text-xs text-stone-400 font-bold">{order.customerInfo.firstName} {order.customerInfo.lastName}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-black text-amber-800">₺{order.total.toLocaleString()}</p>
                  <p className="text-[9px] text-stone-300 font-black uppercase tracking-widest">{new Date(order.createdAt).toLocaleDateString('tr-TR')}</p>
                </div>
              </button>
            )) : (
              <div className="p-20 text-center text-stone-300 italic">Sipariş bulunamadı.</div>
            )}
          </div>
        </div>

        {/* Sipariş Detayı */}
        <div className="lg:col-span-8 min-h-0">
          {selectedOrder ? (
            <div className="bg-white rounded-[2.5rem] border border-stone-100 shadow-sm overflow-hidden h-full flex flex-col">
              <div className="p-8 border-b border-stone-100 flex items-center justify-between bg-stone-50/50 flex-shrink-0">
                <div className="flex flex-col">
                  <h2 className="text-xl font-black text-stone-900 uppercase">SİPARİŞ #{selectedOrder.id.slice(-8)}</h2>
                  <span className="text-[10px] font-mono text-stone-400 font-bold">Müşteri UID: {selectedOrder.userId}</span>
                </div>
                <div className={`px-5 py-2 rounded-full text-[10px] font-black uppercase tracking-widest ${getStatusStyle(selectedOrder.status)}`}>
                  {selectedOrder.status}
                </div>
              </div>

              {/* Kaydırılabilir İçerik Alanı */}
              <div className="flex-1 overflow-y-auto p-10 space-y-8 scrollbar-hide bg-white">
                {/* Müşteri & Not */}
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="space-y-4 bg-stone-50 p-6 rounded-[2rem] border border-stone-100">
                    <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest mb-2">Müşteri Bilgileri</p>
                    <div className="space-y-2">
                      <p className="font-bold text-stone-900 flex items-center"><User size={16} className="mr-2 text-stone-400" />{selectedOrder.customerInfo.firstName} {selectedOrder.customerInfo.lastName}</p>
                      <p className="text-sm text-stone-600 flex items-center"><Mail size={16} className="mr-2 text-stone-400" />{selectedOrder.customerInfo.email}</p>
                      <p className="text-sm text-stone-600 flex items-center"><Phone size={16} className="mr-2 text-stone-400" />{selectedOrder.customerInfo.phone}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4 bg-amber-50/50 p-6 rounded-[2rem] border border-amber-100">
                    <p className="text-[10px] font-black text-amber-800 uppercase tracking-widest mb-2">Sipariş Detayları</p>
                    {selectedOrder.isGiftWrap && (
                      <div className="flex items-center text-amber-800 font-bold text-sm mb-2">
                        <Gift size={16} className="mr-2" />
                        Hediye Paketi İstendi
                      </div>
                    )}
                    {selectedOrder.customerNote ? (
                      <div className="bg-white p-4 rounded-xl border border-amber-100">
                        <p className="text-[10px] font-black text-stone-400 uppercase mb-2 flex items-center"><MessageSquare size={12} className="mr-1" /> Müşteri Notu:</p>
                        <p className="text-sm text-stone-700 italic leading-relaxed">"{selectedOrder.customerNote}"</p>
                      </div>
                    ) : (
                      <p className="text-xs text-stone-400 italic">Müşteri notu bırakmadı.</p>
                    )}
                  </div>
                </div>

                {/* Adres Bilgisi */}
                <div className="bg-stone-50 p-6 rounded-[2rem] border border-stone-100">
                   <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest mb-4">Teslimat Adresi</p>
                   <p className="text-sm text-stone-700 font-medium flex items-start">
                      <MapPin size={16} className="mr-2 mt-0.5 text-stone-400 flex-shrink-0" />
                      {selectedOrder.customerInfo.address} {selectedOrder.customerInfo.apartment} <br />
                      {selectedOrder.customerInfo.district} / {selectedOrder.customerInfo.city} / {selectedOrder.customerInfo.zipCode}
                   </p>
                </div>

                {/* Ürünler & Fiyat */}
                <div className="space-y-4">
                  <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest">Sipariş İçeriği</p>
                  <div className="divide-y divide-stone-50 border border-stone-100 rounded-[2rem] overflow-hidden bg-white">
                    {selectedOrder.items.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-5">
                        <div className="flex items-center space-x-4">
                          <img src={item.product.images[0]} className="w-14 h-14 object-cover rounded-xl border border-stone-50" />
                          <div>
                            <p className="font-bold text-stone-900 text-sm">{item.product.name}</p>
                            <p className="text-xs text-stone-400 font-bold">{item.quantity} Adet x ₺{item.product.price.toLocaleString()}</p>
                          </div>
                        </div>
                        <p className="font-black text-stone-900">₺{(item.product.price * item.quantity).toLocaleString()}</p>
                      </div>
                    ))}
                    
                    {/* Ödeme Özeti */}
                    <div className="p-8 bg-stone-50 space-y-3">
                      <div className="flex justify-between text-stone-500 text-sm font-medium">
                        <span>Ara Toplam</span>
                        <span>₺{(selectedOrder.total + (selectedOrder.discount || 0)).toLocaleString()}</span>
                      </div>
                      {selectedOrder.discount && selectedOrder.discount > 0 && (
                        <div className="flex justify-between text-emerald-600 text-sm font-bold">
                          <div className="flex items-center">
                            <TicketPercent size={14} className="mr-1" />
                            <span>İndirim ({selectedOrder.couponCode})</span>
                          </div>
                          <span>-₺{selectedOrder.discount.toLocaleString()}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center pt-4 border-t border-stone-100">
                        <span className="text-lg font-black text-stone-900 uppercase tracking-widest">NET TOPLAM</span>
                        <span className="text-2xl font-black text-amber-800">₺{selectedOrder.total.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Durum Yönetimi */}
                <div className="p-8 bg-white border border-stone-100 rounded-[2.5rem] space-y-6">
                  <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest">Yönetim Paneli</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <select 
                      value={selectedOrder.status}
                      onChange={e => updateStatus(selectedOrder.id, e.target.value)}
                      className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-amber-500 outline-none font-bold text-stone-900"
                    >
                      <option value="pending">Hazırlanıyor</option>
                      <option value="processing">İşleniyor</option>
                      <option value="shipped">Kargolandı</option>
                      <option value="delivered">Teslim Edildi</option>
                      <option value="cancelled">İptal Edildi</option>
                    </select>
                    <select 
                      value={selectedCargoId}
                      onChange={e => setSelectedCargoId(e.target.value)}
                      className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-indigo-500 outline-none font-bold text-stone-900"
                    >
                      <option value="">Kargo Firması Seçin</option>
                      {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <input 
                      type="text" placeholder="Takip Numarası"
                      value={trackingCode}
                      onChange={e => setTrackingCode(e.target.value)}
                      className="flex-1 bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-indigo-500 outline-none font-bold text-stone-900"
                    />
                    <button onClick={() => saveTrackingCode(selectedOrder.id)} className="bg-indigo-600 text-white px-8 rounded-2xl font-bold hover:bg-indigo-700 transition-all">Kaydet</button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full bg-stone-50 border-2 border-dashed border-stone-200 rounded-[3rem] flex flex-col items-center justify-center text-stone-400">
              <ClipboardList size={48} className="mb-4 opacity-20" />
              <p className="font-medium italic">Sipariş detaylarını görüntülemek için seçim yapın.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminOrders;
